/** Additive VS02 records. Frozen V1 consumer remains unchanged. */
export type Metric = 'TEMPERATURE' | 'PARTICULATE' | 'THERMAL_SCORE';
export const METRIC_SPEC = {
  TEMPERATURE: { unit: 'degC', category: 'RAW_SENSOR' },
  PARTICULATE: { unit: 'ug/m3', category: 'RAW_SENSOR' },
  THERMAL_SCORE: { unit: 'score', category: 'SIMULATED_MODEL_OUTPUT' },
} as const;
export const CONFIG_ACTOR = 'local-demo-operator';
export const CONFIG_INSTANCE = 'forest-demo-1';
export interface ConfigArea {
  areaId: string;
  instanceId: string;
  displayName: string;
  revision: number;
  createdAt: string;
}
export interface ConfigSource {
  sourceId: string;
  instanceId: string;
  areaId: string;
  displayName: string;
  metric: Metric;
  unit: string;
  category: string;
  sourceMode: 'SYNTHETIC_OBSERVATIONS';
  enabled: boolean;
  revision: number;
  epoch: number;
  enabledAt: string | null;
}
export interface MonitoringConfiguration {
  instanceId: string;
  configRevision: number;
  generatedAt: string;
  areas: ConfigArea[];
  sources: ConfigSource[];
}
export interface ConfigIdentity {
  requestId: string;
  expectedRevision: number;
  actor: string;
}
export interface CreateArea extends ConfigIdentity {
  instanceId: string;
  areaId: string;
  displayName: string;
}
export interface CreateSource extends ConfigIdentity {
  instanceId: string;
  areaId: string;
  sourceId: string;
  displayName: string;
  metric: Metric;
  unit: string;
  category: string;
}
export interface Rename extends ConfigIdentity {
  displayName: string;
}
export interface SetEnabled extends ConfigIdentity {
  enabled: boolean;
  reason: string;
}
export interface ConfigReceipt {
  requestId: string;
  action: string;
  objectId: string;
  configRevision: number;
  objectRevision: number;
  epoch: number;
  recordedAt: string;
}
export interface ConfigAudit {
  requestId: string;
  action: string;
  objectId: string;
  beforeRevision: number;
  afterRevision: number;
  beforeConfigRevision: number;
  afterConfigRevision: number;
  actor: string;
  reason: string;
  recordedAt: string;
}
export interface ConfigAuditPage {
  items: ConfigAudit[];
  total: number;
  offset: number;
  limit: number;
  generatedAt: string;
}
export type CoverageKind = 'NO_SOURCES' | 'ALL_DISABLED' | 'COVERAGE_GAP' | 'COMPLETE';
export interface AreaCoverage {
  areaId: string;
  configuredSources: number;
  enabledSources: number;
  recentSources: number;
  eligibleSources: number;
  coveredMetrics: Metric[];
  missingMetrics: Metric[];
  coverage: CoverageKind;
}
export interface RuntimeSource {
  sourceId: string;
  epoch: number;
  receiveHealth: 'NO_DATA' | 'RECENT' | 'STALE';
  eligible: boolean;
  normalSamples: number;
  latestObservedAt: string | null;
  latestReceivedAt: string | null;
}
export interface MonitoringNetwork {
  configRevision: number;
  generatedAt: string;
  sourceMode: 'SYNTHETIC_OBSERVATIONS';
  areas: AreaCoverage[];
  sources: RuntimeSource[];
}
export interface ObservationBasis {
  reference: string;
  configRevision: number;
  sourceRevision: number;
  epoch: number;
  basis: string;
  processingStatus: string;
  processingReason: string;
}
export interface WarningBasis {
  warningId: string;
  revision: number;
  configRevision: number;
  basis: string;
  requiredSourceIds: string[];
  missingSourceIds: string[];
  missingMetrics: Metric[];
  changeKind: string;
}
export interface WarningConfiguration {
  warningId: string;
  revisions: WarningBasis[];
  observations: ObservationBasis[];
}
export interface ConfigurationPort {
  configuration(signal: AbortSignal): Promise<MonitoringConfiguration>;
  network(signal: AbortSignal): Promise<MonitoringNetwork>;
  history(offset: number, limit: number, signal: AbortSignal): Promise<ConfigAuditPage>;
  warningConfiguration(id: string, signal: AbortSignal): Promise<WarningConfiguration>;
  observationConfiguration(reference: string, signal: AbortSignal): Promise<ObservationBasis>;
  createArea(body: CreateArea, signal: AbortSignal): Promise<ConfigReceipt>;
  createSource(body: CreateSource, signal: AbortSignal): Promise<ConfigReceipt>;
  renameArea(id: string, body: Rename, signal: AbortSignal): Promise<ConfigReceipt>;
  renameSource(id: string, body: Rename, signal: AbortSignal): Promise<ConfigReceipt>;
  setEnabled(id: string, body: SetEnabled, signal: AbortSignal): Promise<ConfigReceipt>;
}
